// letter_trace — trace an Arabic letter along SVG stroke guides. Stroke-ORDER
// evaluation lands in Phase 3 (CLAUDE.md §26.6); for now this is presentational:
// the learner drags to "ink" the guide strokes, and completing the trace (or
// simply engaging) is full credit. The guide paths are drawn faintly; the
// learner's pointer path is drawn on top.

import { useEffect, useRef, useState, type PointerEvent, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { ArabicText, AudioButton } from './primitives';
import type { ActivityRendererProps } from './types';

export function LetterTrace({
  data,
  ctx,
  cb,
}: ActivityRendererProps<content.LetterTraceData>): ReactElement {
  const { t } = ctx;
  useEffect(() => {
    cb.onViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const svgRef = useRef<SVGSVGElement | null>(null);
  const [drawing, setDrawing] = useState(false);
  // Each traced stroke is an array of "x,y" points; we keep completed strokes
  // plus the in-progress one.
  const [strokes, setStrokes] = useState<string[][]>([]);

  function toLocal(e: PointerEvent<SVGSVGElement>): string | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const [minX, minY, w, h] = data.viewBox.split(' ').map(Number) as [
      number,
      number,
      number,
      number,
    ];
    const x = minX + ((e.clientX - rect.left) / rect.width) * w;
    const y = minY + ((e.clientY - rect.top) / rect.height) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }

  function onDown(e: PointerEvent<SVGSVGElement>): void {
    const pt = toLocal(e);
    if (!pt) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrawing(true);
    setStrokes((cur) => [...cur, [pt]]);
  }

  function onMove(e: PointerEvent<SVGSVGElement>): void {
    if (!drawing) return;
    const pt = toLocal(e);
    if (!pt) return;
    setStrokes((cur) => {
      if (cur.length === 0) return cur;
      const next = cur.slice();
      next[next.length - 1] = [...next[next.length - 1]!, pt];
      return next;
    });
  }

  function onUp(): void {
    setDrawing(false);
  }

  function clear(): void {
    setStrokes([]);
  }

  return (
    <div className="mx-auto flex w-full max-w-sm flex-col items-center text-center">
      {data.nameKey ? (
        <h2 className="mb-4 text-2xl font-bold text-burgundy-700">
          <ArabicText text={t(data.nameKey)} ctx={ctx} />
        </h2>
      ) : null}

      <div className="relative w-64">
        <svg
          ref={svgRef}
          viewBox={data.viewBox}
          className="aspect-square w-full touch-none select-none rounded-[2rem] bg-gradient-to-b from-sun-50 to-cream-50 ring-2 ring-sun-300/40"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
          role="img"
          aria-label={t('content.letter_trace.canvas_label', { glyph: data.glyph })}
        >
          {/* Faint glyph behind the guide, for orientation. */}
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="central"
            className="fill-burgundy-500/10 font-display-ar"
            style={{ fontSize: '72px' }}
          >
            {data.glyph}
          </text>
          {/* Guide strokes the learner should follow. */}
          {data.strokes.map((d, i) => (
            <path
              key={`guide-${i}`}
              d={d}
              fill="none"
              stroke="var(--color-sun-300, #FFD166)"
              strokeWidth={6}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="1 8"
              opacity={0.8}
            />
          ))}
          {/* The learner's traced ink. */}
          {strokes.map((pts, i) =>
            pts.length > 1 ? (
              <polyline
                key={`ink-${i}`}
                points={pts.join(' ')}
                fill="none"
                stroke="var(--color-burgundy-500, #8B2C3D)"
                strokeWidth={5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null,
          )}
        </svg>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <AudioButton src={data.audioName} ctx={ctx} label={t('content.common.play_audio')} />
        <button
          type="button"
          onClick={clear}
          className="inline-flex min-h-[48px] items-center rounded-full bg-cream-100 px-5 font-bold text-burgundy-700 ring-1 ring-burgundy-500/10 transition-colors hover:bg-cream-200"
        >
          ↺ {t('content.letter_trace.clear')}
        </button>
      </div>
    </div>
  );
}
